import { useState, useCallback } from 'react';
import weighingService from '@/services/weighing';
import qualityAnalysisService from '@/services/qualityAnalysis';
import { useWeighing, useCurrentWeighing } from '@/contexts/WeighingContext';
import { resolveWeighingProcess } from '@/utils/weighingProcess';
import { getDisplayPeseeCode } from '@/utils/ticketSequences';
import {
  computePoidsNet,
  resolvePoidsSacs,
  resolveNbSacsAcceptes,
} from '@/utils/weighingNetCalc';

export interface WeighingWorkflowState {
  currentStep: number;
  weighingId?: string;
  weighingCode?: string;
  tenantId?: string;
  siteId?: string;
  grossWeight?: number;
  tareWeight?: number;
  netWeight?: number;
  analysisData?: any;
  isCompleted: boolean;
  isArchived: boolean;
  
  // Nouveau : Type de processus de pesée
  weighingProcess: 'standard' | 'inverted'; // 'standard' = Processus 1, 'inverted' = Processus 2
}

export interface WeighingFormData {
  // Type de pesée
  typePesee: string;
  produit: string;
  conditionnement: string;
  
  // Informations véhicule
  vehicule: string;
  remorque: string;
  transporteur: string;
  chauffeur: string;
  convoyeur?: string;
  
  // Origine
  provenance: string;
  connaissement: string;
  
  // Destination
  destination: string;
  magasin: string;
  numeroLot: string;
  
  // Client/Fournisseur/Exportateur
  client: string;
  fournisseur: string;
  exportateur: string;
  numeroTransfert: string;
  sticker: string;
  
  // Analyse qualité
  nbSacsDeclares: number;
  poidsDeclares: number;
  nbSacsSains: number;
  nbSacsRecond: number;
  nbSacsHumides: number;
  nbSacsDechires: number;
  nbSacsRejetes: number;
  nbSacsAcceptes: number;
  poidsAcceptes: number;
  refractionHum: number;
  refractionQlt: number;
  observations: string;
}

const useWeighingWorkflow = (_pontBasculeId: string = '1') => {
  const { setCurrentWeighing, updateWeighing } = useWeighing();
  const currentWeighing = useCurrentWeighing();
  
  const [workflowState, setWorkflowState] = useState<WeighingWorkflowState>({
    currentStep: 1,
    isCompleted: false,
    isArchived: false,
    weighingProcess: 'standard' // Processus par défaut
  });

  const [formData, setFormData] = useState<WeighingFormData>({
    typePesee: 'reception',
    produit: '', // Pas de produit par défaut
    conditionnement: '', // Conditionnement par défaut (vide)
    vehicule: '', // Pas de véhicule par défaut
    remorque: '',
    transporteur: '',
    chauffeur: '', // Pas de chauffeur par défaut
    convoyeur: '',
    provenance: '',
    connaissement: '',
    destination: '',
    magasin: '',
    numeroLot: '',
    client: '',
    fournisseur: '', // Pas de fournisseur par défaut
    exportateur: '',
    numeroTransfert: '',
    sticker: '',
    nbSacsDeclares: 0,
    poidsDeclares: 0,
    nbSacsSains: 0,
    nbSacsRecond: 0,
    nbSacsHumides: 0,
    nbSacsDechires: 0,
    nbSacsRejetes: 0,
    nbSacsAcceptes: 0,
    poidsAcceptes: 0,
    refractionHum: 0,
    refractionQlt: 0,
    observations: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Étape 1: Créer ou mettre à jour la pesée avec le poids (brut ou tare selon le processus)
  const saveGrossWeight = useCallback(async (weight: number) => {
    setIsLoading(true);
    setError(null);
    
    // Valider le poids reçu
    if (typeof weight !== 'number' || isNaN(weight) || weight <= 0) {
      setError('Poids invalide reçu');
      setIsLoading(false);
      return false;
    }
    
    // Valider qu'un produit est sélectionné
    if (!formData.produit || formData.produit === '') {
      setError('Veuillez sélectionner un produit avant d\'enregistrer le poids');
      setIsLoading(false);
      return false;
    }
    
    // Vérifier que le poids respecte la limite du modèle (max_digits=10, decimal_places=2)
    if (weight > 99999999.99) {
      setError('Poids trop élevé (maximum: 99,999,999.99 kg)');
      setIsLoading(false);
      return false;
    }
    
    // Déterminer le type de poids selon le processus et l'étape
    const isInvertedProcess = workflowState.weighingProcess === 'inverted';
    const isStep1 = workflowState.currentStep === 1;
    let isTareStep = isInvertedProcess && isStep1;
    // Une pesée déjà commencée en brut ne doit jamais réécrire ce poids dans tare
    const existingBrut = currentWeighing?.poids_brut
      ? parseFloat(String(currentWeighing.poids_brut))
      : 0;
    if (isTareStep && existingBrut > 0) {
      isTareStep = false;
    }
    
    try {
      if (workflowState.weighingId) {
        // Mettre à jour la pesée existante
        const updateData: any = {};
        if (isTareStep) {
          updateData.poids_tare = weight;
          // Inversé étape 1 : tare enregistrée (on attend encore brut, pas une tare)
          updateData.statut = 'TARE_ENREGISTREE';
          updateData.processus = 'inverted';
        } else {
          updateData.poids_brut = weight;
          updateData.statut = 'BRUT_ENREGISTRE';
          updateData.processus = workflowState.weighingProcess || 'standard';
        }
        
        await weighingService.updatePesée(parseInt(workflowState.weighingId), updateData);
        
        // Mettre à jour le workflow state
        setWorkflowState(prev => ({
          ...prev,
          ...(isTareStep ? { tareWeight: weight } : { grossWeight: weight })
        }));
        
        // Recharger la pesée complète
        try {
          const fullWeighingResponse = await weighingService.getPesée(parseInt(workflowState.weighingId));
          if (fullWeighingResponse.success && fullWeighingResponse.data) {
            setCurrentWeighing(fullWeighingResponse.data);
          } else {
            updateWeighing(updateData);
          }
        } catch (error) {
          console.error('❌ Erreur lors du rechargement de la pesée complète:', error);
          updateWeighing(updateData);
        }
        
        return true;
      } else {
        // Vérifier que les champs obligatoires sont présents
        if (!formData.produit) {
          setError('Le produit est obligatoire pour créer une pesée');
          setIsLoading(false);
          return null;
        }

        const produitId = parseInt(formData.produit);
        if (isNaN(produitId)) {
          setError('ID de produit invalide');
          setIsLoading(false);
          return null;
        }

        // Préparer les données selon le processus
        const weighingData: any = {
          type_pesee: formData.typePesee,
          produit: produitId,
          vehicule: formData.vehicule ? parseInt(formData.vehicule) : undefined,
          remorque: formData.remorque && formData.remorque !== '' ? parseInt(formData.remorque) : undefined,
          transporteur: formData.transporteur && formData.transporteur !== '' ? parseInt(formData.transporteur) : undefined,
          chauffeur: formData.chauffeur ? parseInt(formData.chauffeur) : undefined,
          convoyeur: formData.convoyeur && formData.convoyeur !== '' ? parseInt(formData.convoyeur) : undefined,
          provenance: formData.provenance,
          connaissement: formData.connaissement,
          destination: formData.destination,
          magasin: formData.magasin && formData.magasin !== '' ? parseInt(formData.magasin) : undefined,
          numero_lot: formData.numeroLot,
          client: formData.client && formData.client !== '' ? parseInt(formData.client) : undefined,
          fournisseur: formData.fournisseur ? parseInt(formData.fournisseur) : undefined,
          exportateur: formData.exportateur && formData.exportateur !== '' ? parseInt(formData.exportateur) : undefined,
          numero_transfert: formData.numeroTransfert,
          sticker: formData.sticker,
          nb_sacs_declares: formData.nbSacsDeclares || 0,
          poids_declares: formData.poidsDeclares || 0,
          processus: workflowState.weighingProcess, // Enregistrer le processus
        };
        
        // Enregistrer dans le bon champ selon le processus
        if (isTareStep) {
          // En mode inversé, on crée la pesée avec seulement le poids_tare
          // Le poids_brut sera enregistré à l'étape 3
          // Statut TARE_ENREGISTREE (PAS EN_ATTENTE_TARE) pour ne pas être
          // confondu avec une pesée standard en attente de tare.
          weighingData.poids_tare = weight;
          weighingData.statut = 'TARE_ENREGISTREE';
          weighingData.processus = 'inverted';
        } else {
          weighingData.poids_brut = weight;
          weighingData.statut = 'BRUT_ENREGISTRE';
          weighingData.processus = workflowState.weighingProcess || 'standard';
        }

        // Créer une nouvelle pesée
        const newWeighing = await weighingService.createPesée(weighingData);
        
        const weighingId = (newWeighing as any).data?.id || (newWeighing as any).id || '';
        const weighingDataResponse = (newWeighing as any).data || newWeighing;
        
        // Racine PES-AAMMJJ-HHMM-RR + séquence globale
        const weighingCode = getDisplayPeseeCode(weighingDataResponse || newWeighing);
        
        setWorkflowState(prev => ({
          ...prev,
          weighingId: weighingId?.toString(),
          weighingCode, // Utiliser le reference du backend
          ...(isTareStep ? { tareWeight: weight } : { grossWeight: weight }),
          tenantId: '1', // TODO: Récupérer depuis le contexte
          siteId: '1' // TODO: Récupérer depuis le contexte
        }));

        // Sauvegarder les valeurs d'autocomplétion (provenance et destination) si elles sont renseignées
        try {
          if (formData.provenance && formData.provenance.trim()) {
            await weighingService.createOrIncrementAutocompleteValue('provenance', formData.provenance);
          }
          if (formData.destination && formData.destination.trim()) {
            await weighingService.createOrIncrementAutocompleteValue('destination', formData.destination);
          }
        } catch (error) {
          // Ne pas bloquer le processus si la sauvegarde des valeurs d'autocomplétion échoue
          console.warn('Erreur lors de la sauvegarde des valeurs d\'autocomplétion:', error);
        }

        // Recharger la pesée avec toutes les relations
        try {
          const fullWeighingResponse = await weighingService.getPesée(parseInt(weighingId));
          if (fullWeighingResponse.success && fullWeighingResponse.data) {
            const fullWeighing = fullWeighingResponse.data;
            setCurrentWeighing(fullWeighing);
            // Mettre à jour le code de pesée avec le reference du backend
            if (fullWeighing.reference) {
              setWorkflowState(prev => ({
                ...prev,
                weighingCode: getDisplayPeseeCode(fullWeighing)
              }));
            }
          } else {
            setCurrentWeighing(weighingDataResponse);
          }
        } catch (error) {
          console.error('❌ Erreur lors du rechargement de la pesée complète:', error);
          setCurrentWeighing(weighingDataResponse);
        }

        return weighingDataResponse;
      }
    } catch (err: any) {
      // Log détaillé de l'erreur
      console.error('❌ Erreur saveGrossWeight - Détails:', {
        message: err?.message,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        errors: err?.response?.data?.errors,
        fullError: err
      });
      
      const errorMessage = err?.response?.data?.message || 
                          (err?.response?.data?.errors ? JSON.stringify(err.response.data.errors) : null) ||
                          err?.message || 
                          `Erreur lors de l'enregistrement du poids ${isTareStep ? 'tare' : 'brut'}`;
      setError(errorMessage);
      console.error('Erreur saveGrossWeight:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId, workflowState.weighingProcess, workflowState.currentStep, formData, setCurrentWeighing, updateWeighing]);

  // Étape 2: Enregistrer l'analyse qualité
  const saveQualityAnalysis = useCallback(async (analysisData: any) => {
    if (!workflowState.weighingId) {
      setError('Aucune pesée active');
      return false;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Créer l'analyse qualité via l'API dédiée
      const response = await qualityAnalysisService.createQualityAnalysisForPesee(
        parseInt(workflowState.weighingId),
        {
          moisture: analysisData.moisture,
          proteins: analysisData.proteins,
          hectoliter_weight: analysisData.hectoliter_weight,
          impurities: analysisData.impurities,
          broken_grains: analysisData.broken_grains,
          germinated_grains: analysisData.germinated_grains,
          quality_decision: analysisData.quality_decision || 'ACCEPTE',
          observations: analysisData.observations
        }
      );

      if (response.success) {
        setWorkflowState(prev => ({
          ...prev,
          analysisData: response.data
        }));

        // Recharger la pesée complète avec toutes les relations après l'analyse qualité
        try {
          const fullWeighingResponse = await weighingService.getPesée(parseInt(workflowState.weighingId));
          if (fullWeighingResponse.success && fullWeighingResponse.data) {
            setCurrentWeighing(fullWeighingResponse.data);
          }
        } catch (error) {
          console.error('❌ Erreur lors du rechargement de la pesée après analyse qualité:', error);
        }

        return true;
      } else {
        setError(response.message);
        return false;
      }
    } catch (err) {
      setError('Erreur lors de l\'enregistrement de l\'analyse');
      console.error('Erreur saveQualityAnalysis:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId]);

  // Étape 3: Enregistrer le poids tare et calculer le poids net
  const saveTareWeight = useCallback(async (weight: number) => {
    // Source de vérité : pesée en base + état workflow (évite de basculer en standard par erreur)
    const resolvedProcess = resolveWeighingProcess(
      currentWeighing || {
        processus: workflowState.weighingProcess,
        poids_brut: workflowState.grossWeight,
        poids_tare: workflowState.tareWeight,
      },
    );
    const isInvertedProcess =
      workflowState.weighingProcess === 'inverted' || resolvedProcess === 'inverted';
    const isStep3 = workflowState.currentStep === 3;
    
    // En mode inversé à l'étape 3, on enregistre le poids brut
    if (isInvertedProcess && isStep3) {
      // Utiliser workflowState.weighingId ou currentWeighing?.id
      const weighingId = workflowState.weighingId || currentWeighing?.id?.toString();
      if (!weighingId) {
        setError('Aucune pesée active');
        return false;
      }

      // En mode inversé, on a déjà le poids tare (étape 1), maintenant on enregistre le poids brut
      // Récupérer le poids tare depuis workflowState ou currentWeighing
      let tareWeight = workflowState.tareWeight;
      if (!tareWeight && currentWeighing?.poids_tare) {
        tareWeight = parseFloat(currentWeighing.poids_tare.toString());
      }
      if (!tareWeight) {
        // Essayer de récupérer depuis l'API
        const currentWeighingData = await weighingService.getPesée(parseInt(weighingId));
        if (currentWeighingData.success && currentWeighingData.data?.poids_tare) {
          tareWeight = parseFloat(currentWeighingData.data.poids_tare.toString());
        }
      }
      
      if (!tareWeight) {
        setError('Poids tare manquant (doit être enregistré à l\'étape 1 en mode inversé)');
        return false;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        const typePesee = currentWeighing?.type_pesee || formData.typePesee || 'reception';
        const conditionnementCapacite =
          (currentWeighing as any)?.produit_info?.conditionnement_info?.poids_tare ||
          (currentWeighing as any)?.conditionnement_info?.poids_tare ||
          50;

        const formSacs = {
          nbSacsSains: formData.nbSacsSains,
          nbSacsRecond: formData.nbSacsRecond,
          nbSacsDeclares: formData.nbSacsDeclares,
          nbSacsAcceptes: formData.nbSacsAcceptes,
          poidsAcceptes: formData.poidsAcceptes,
          poidsDeclares: formData.poidsDeclares,
        };

        const poidsSacs = resolvePoidsSacs({
          typePesee,
          form: formSacs,
          weighing: currentWeighing || undefined,
          conditionnementCapacite,
        });
        const nbSacsAcceptes = resolveNbSacsAcceptes({
          typePesee,
          form: formSacs,
          weighing: currentWeighing || undefined,
        });
        const poidsAcceptes = formData.poidsAcceptes || poidsSacs || 0;

        // Formule: Poids Brut - (Poids Tare + Poids Sacs)
        const netWeight = computePoidsNet(weight, tareWeight, poidsSacs);

        if (weight <= tareWeight) {
          setError(`Poids brut (${weight}) doit être > tare (${tareWeight})`);
          return false;
        }
        
        await weighingService.updatePesée(parseInt(weighingId), {
          poids_brut: weight,
          poids_net: netWeight,
          processus: 'inverted',
          nb_sacs_declares: formData.nbSacsDeclares || currentWeighing?.nb_sacs_declares || 0,
          nb_sacs_sains: formData.nbSacsSains || currentWeighing?.nb_sacs_sains || 0,
          nb_sacs_recond: formData.nbSacsRecond || currentWeighing?.nb_sacs_recond || 0,
          nb_sacs_humides: formData.nbSacsHumides || currentWeighing?.nb_sacs_humides || 0,
          nb_sacs_dechires: formData.nbSacsDechires || currentWeighing?.nb_sacs_dechires || 0,
          nb_sacs_rejetes: formData.nbSacsRejetes || currentWeighing?.nb_sacs_rejetes || 0,
          nb_sacs_acceptes: nbSacsAcceptes,
          poids_acceptes: poidsAcceptes,
          poids_declares: formData.poidsDeclares || currentWeighing?.poids_declares || 0,
          statut: 'BRUT_ENREGISTRE',
        });

        setWorkflowState(prev => ({
          ...prev,
          weighingId: prev.weighingId || weighingId,
          grossWeight: weight,
          netWeight,
          isCompleted: true
        }));

        // Recharger la pesée complète
        try {
          const fullWeighingResponse = await weighingService.getPesée(parseInt(weighingId));
          if (fullWeighingResponse.success && fullWeighingResponse.data) {
            setCurrentWeighing(fullWeighingResponse.data);
          } else {
            updateWeighing({
              poids_brut: weight,
              poids_net: netWeight,
              statut: 'COMPLETEE'
            });
          }
        } catch (error) {
          console.error('❌ Erreur lors du rechargement de la pesée complète:', error);
          updateWeighing({
            poids_brut: weight,
            poids_net: netWeight,
            statut: 'COMPLETEE'
          });
        }

        return true;
      } catch (err) {
        setError('Erreur lors de l\'enregistrement du poids brut');
        console.error('Erreur saveTareWeight (mode inversé):', err);
        return false;
      } finally {
        setIsLoading(false);
      }
    }
    
    // Mode standard : logique existante (étape 3 = poids tare)
    const brutFromPesee = currentWeighing?.poids_brut
      ? parseFloat(currentWeighing.poids_brut.toString())
      : undefined;
    const grossWeight = workflowState.grossWeight || brutFromPesee;
    if (!workflowState.weighingId || !grossWeight) {
      setError('Poids brut manquant');
      return false;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Déterminer le type de pesée
      const typePesee = currentWeighing?.type_pesee || formData.typePesee || 'reception';
      const conditionnementCapacite =
        (currentWeighing as any)?.produit_info?.conditionnement_info?.poids_tare ||
        (currentWeighing as any)?.conditionnement_info?.poids_tare ||
        50;

      const formSacs = {
        nbSacsSains: formData.nbSacsSains,
        nbSacsRecond: formData.nbSacsRecond,
        nbSacsDeclares: formData.nbSacsDeclares,
        nbSacsAcceptes: formData.nbSacsAcceptes,
        poidsAcceptes: formData.poidsAcceptes,
        poidsDeclares: formData.poidsDeclares,
      };

      const poidsSacs = resolvePoidsSacs({
        typePesee,
        form: formSacs,
        weighing: currentWeighing || undefined,
        conditionnementCapacite,
      });
      const nbSacsAcceptes = resolveNbSacsAcceptes({
        typePesee,
        form: formSacs,
        weighing: currentWeighing || undefined,
      });
      const poidsAcceptes = formData.poidsAcceptes || poidsSacs || 0;
      
      // Formule: Poids Brut - (Poids Tare + Poids Sacs)
      const netWeight = computePoidsNet(grossWeight, weight, poidsSacs);
      
      await weighingService.updatePesée(parseInt(workflowState.weighingId), {
        poids_tare: weight,
        poids_net: netWeight,
        processus: workflowState.weighingProcess || 'standard',
        nb_sacs_declares: formData.nbSacsDeclares || currentWeighing?.nb_sacs_declares || 0,
        nb_sacs_sains: formData.nbSacsSains || currentWeighing?.nb_sacs_sains || 0,
        nb_sacs_recond: formData.nbSacsRecond || currentWeighing?.nb_sacs_recond || 0,
        nb_sacs_humides: formData.nbSacsHumides || currentWeighing?.nb_sacs_humides || 0,
        nb_sacs_dechires: formData.nbSacsDechires || currentWeighing?.nb_sacs_dechires || 0,
        nb_sacs_rejetes: formData.nbSacsRejetes || currentWeighing?.nb_sacs_rejetes || 0,
        nb_sacs_acceptes: nbSacsAcceptes,
        poids_acceptes: poidsAcceptes,
        poids_declares: formData.poidsDeclares || currentWeighing?.poids_declares || 0,
        statut: 'TARE_ENREGISTREE'
      });

      setWorkflowState(prev => ({
        ...prev,
        tareWeight: weight,
        netWeight,
        isCompleted: true
      }));

      // Recharger la pesée complète avec toutes les relations
      try {
        const fullWeighingResponse = await weighingService.getPesée(parseInt(workflowState.weighingId));
        if (fullWeighingResponse.success && fullWeighingResponse.data) {
          setCurrentWeighing(fullWeighingResponse.data);
        } else {
          // Fallback sur la mise à jour partielle
          updateWeighing({
            poids_tare: weight,
            poids_net: netWeight,
            statut: 'TARE_ENREGISTREE'
          });
        }
      } catch (error) {
        console.error('❌ Erreur lors du rechargement de la pesée complète:', error);
        // Fallback sur la mise à jour partielle
        updateWeighing({
          poids_tare: weight,
          poids_net: netWeight,
          statut: 'TARE_ENREGISTREE'
        });
      }

      return true;
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du poids tare');
      console.error('Erreur saveTareWeight:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId, workflowState.grossWeight, workflowState.tareWeight, workflowState.weighingProcess, workflowState.currentStep, currentWeighing, setCurrentWeighing, updateWeighing]);

  // Archiver la pesée
  const archiveWeighing = useCallback(async (shouldArchive: boolean) => {
    if (!workflowState.weighingId) {
      setError('Aucune pesée active');
      return false;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      if (shouldArchive) {
        await weighingService.updatePesée(parseInt(workflowState.weighingId), {
          // Archivage de la pesée
        } as any);

        setWorkflowState(prev => ({
          ...prev,
          isArchived: true
        }));
      }

      return true;
    } catch (err) {
      setError('Erreur lors de l\'archivage');
      console.error('Erreur archiveWeighing:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId]);

  // Navigation entre les étapes
  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 4) {
      setWorkflowState(prev => ({
        ...prev,
        currentStep: step
      }));
    }
  }, []);

  // Fonction pour vérifier si l'analyse qualité est requise
  const isQualityAnalysisRequired = useCallback(() => {
    // Si le produit n'exige pas d'analyse qualité, on peut sauter l'étape 2
    // Cette logique sera basée sur le champ qualite_requise du produit
    // Pour l'instant, on retourne true par défaut
    return true;
  }, [formData.produit]);

  // Fonction pour déterminer le processus de pesée
  const determineWeighingProcess = useCallback((_typePesee: string, _produitType?: string) => {
    // Logique pour déterminer le processus selon le type de pesée et le produit
   
    // Pour l'instant, utiliser le processus standard par défaut
    // On pourrait ajouter une logique basée sur le type de pesée ou le produit
    return 'standard';
  }, []);

  // Fonction pour obtenir l'ordre des étapes selon le processus
  const getStepOrder = useCallback((process: 'standard' | 'inverted') => {
    if (process === 'inverted') {
      return {
        1: 'tare',      // Étape 1 : Pesée tare (vide)
        2: 'quality',   // Étape 2 : Analyse qualité (optionnelle)
        3: 'gross',     // Étape 3 : Pesée brute (chargé)
        4: 'ticket'     // Étape 4 : Ticket final
      };
    } else {
      return {
        1: 'gross',     // Étape 1 : Pesée brute (chargé)
        2: 'quality',   // Étape 2 : Analyse qualité (optionnelle)
        3: 'tare',      // Étape 3 : Pesée tare (vide)
        4: 'ticket'     // Étape 4 : Ticket final
      };
    }
  }, []);

  // Fonction pour obtenir le libellé de l'étape selon le processus
  const getStepLabel = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    
    switch (stepType) {
      case 'gross':
        return 'Pesée brute (chargé)';
      case 'tare':
        return 'Pesée tare (vide)';
      case 'quality':
        return 'Analyse qualité';
      case 'ticket':
        return 'Ticket final';
      default:
        return 'Étape inconnue';
    }
  }, [getStepOrder]);

  // Fonction pour obtenir la description de l'étape
  const getStepDescription = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    
    switch (stepType) {
      case 'gross':
        return 'Capture du poids brut (véhicule chargé)';
      case 'tare':
        return 'Capture du poids tare (véhicule vide)';
      case 'quality':
        return 'Saisie des paramètres de qualité';
      case 'ticket':
        return 'Affichage et archivage du ticket';
      default:
        return 'Description inconnue';
    }
  }, [getStepOrder]);

  // Fonction pour vérifier si l'étape actuelle est une pesée
  const isWeighingStep = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    return stepType === 'gross' || stepType === 'tare';
  }, [getStepOrder]);

  // Fonction pour obtenir le type de pesée de l'étape actuelle
  const getCurrentWeighingType = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    return stepType === 'gross' ? 'gross' : stepType === 'tare' ? 'tare' : null;
  }, [getStepOrder]);

  // Fonction pour obtenir les instructions pour l'utilisateur
  const getStepInstructions = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    
    switch (stepType) {
      case 'gross':
        return process === 'inverted' 
          ? 'Chargez le véhicule et capturez le poids brut'
          : 'Le véhicule est chargé, capturez le poids brut';
      case 'tare':
        return process === 'inverted'
          ? 'Le véhicule est vide, capturez le poids tare'
          : 'Videz le véhicule et capturez le poids tare';
      case 'quality':
        return 'Effectuez l\'analyse qualité si nécessaire';
      case 'ticket':
        return 'Générez le ticket final';
      default:
        return 'Instructions non définies';
    }
  }, [getStepOrder]);

  // Fonction pour initialiser / synchroniser le processus de pesée
  // resetStep=true uniquement pour une NOUVELLE pesée (pas au chargement d'une existante)
  const initializeWeighingProcess = useCallback((
    process: 'standard' | 'inverted' = 'standard',
    resetStep = false,
  ) => {
    setWorkflowState(prev => ({
      ...prev,
      weighingProcess: process,
      ...(resetStep ? { currentStep: 1 } : {}),
    }));
  }, []);

  // Fonction pour changer de processus (si nécessaire)
  const switchWeighingProcess = useCallback((newProcess: 'standard' | 'inverted') => {
    setWorkflowState(prev => ({
      ...prev,
      weighingProcess: newProcess,
      currentStep: 1, // Redémarrer depuis le début
      grossWeight: undefined,
      tareWeight: undefined,
      netWeight: undefined
    }));
  }, []);

  const nextStep = useCallback(() => {
    if (workflowState.currentStep < 4) {
      let nextStepNumber = workflowState.currentStep + 1;
      
      // Si on est à l'étape 1 et que l'analyse qualité n'est pas requise, sauter l'étape 2
      if (workflowState.currentStep === 1 && !isQualityAnalysisRequired()) {
        nextStepNumber = 3; // Passer directement à l'étape 3
      }
      
      setWorkflowState(prev => ({
        ...prev,
        currentStep: nextStepNumber
      }));
    }
  }, [workflowState.currentStep, isQualityAnalysisRequired]);

  const previousStep = useCallback(() => {
    if (workflowState.currentStep > 1) {
      let prevStepNumber = workflowState.currentStep - 1;
      
      // Si on est à l'étape 3 et que l'analyse qualité n'est pas requise, retourner à l'étape 1
      if (workflowState.currentStep === 3 && !isQualityAnalysisRequired()) {
        prevStepNumber = 1; // Retourner directement à l'étape 1
      }
      
      setWorkflowState(prev => ({
        ...prev,
        currentStep: prevStepNumber
      }));
    }
  }, [workflowState.currentStep, isQualityAnalysisRequired]);

  // Mise à jour des données du formulaire
  const updateFormData = useCallback((field: keyof WeighingFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Réinitialiser le workflow (optionnel: processus pour une nouvelle pesée)
  const resetWorkflow = useCallback((process: 'standard' | 'inverted' = 'standard') => {
    setWorkflowState({
      currentStep: 1,
      weighingId: undefined,
      weighingCode: undefined,
      tenantId: undefined,
      siteId: undefined,
      grossWeight: undefined,
      tareWeight: undefined,
      netWeight: undefined,
      analysisData: undefined,
      isCompleted: false,
      isArchived: false,
      weighingProcess: process
    });
    setFormData({
      typePesee: 'reception',
      produit: '',
      conditionnement: '',
      vehicule: '',
      remorque: '',
      transporteur: '',
      chauffeur: '',
      provenance: '',
      connaissement: '',
      destination: '',
      magasin: '',
      numeroLot: '',
      client: '',
      fournisseur: '',
      exportateur: '',
      numeroTransfert: '',
      sticker: '',
      nbSacsDeclares: 0,
      poidsDeclares: 0,
      nbSacsSains: 0,
      nbSacsRecond: 0,
      nbSacsHumides: 0,
      nbSacsDechires: 0,
      nbSacsRejetes: 0,
      nbSacsAcceptes: 0,
      poidsAcceptes: 0,
      refractionHum: 0,
      refractionQlt: 0,
      observations: ''
    });
    setError(null);
  }, []);

  // Note: Le code de pesée (reference) est maintenant généré par le backend
  // Plus besoin de générer un code côté frontend

  // Fonction pour recharger les données complètes de la pesée actuelle
  const refreshCurrentWeighing = useCallback(async () => {
    if (!workflowState.weighingId) {
      return false;
    }

    try {
      const fullWeighingResponse = await weighingService.getPesée(parseInt(workflowState.weighingId));
      if (fullWeighingResponse.success && fullWeighingResponse.data) {
        setCurrentWeighing(fullWeighingResponse.data);
        return true;
      } else {
        console.error('❌ Échec du rechargement de la pesée:', fullWeighingResponse.message);
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur lors du rechargement manuel de la pesée:', error);
      return false;
    }
  }, [workflowState.weighingId, setCurrentWeighing]);

  // Vérifier si on peut passer à l'étape suivante
  const canProceedToNextStep = useCallback((step: number) => {
    const isInvertedProcess = workflowState.weighingProcess === 'inverted';
    
    switch (step) {
      case 1:
        // En mode inversé, étape 1 = tare, sinon = brut
        if (isInvertedProcess) {
          return !!workflowState.tareWeight && !!formData.vehicule && !!formData.produit;
        } else {
          return !!workflowState.grossWeight && !!formData.vehicule && !!formData.produit;
        }
      case 2:
        return !!workflowState.analysisData;
      case 3:
        // En mode inversé, étape 3 = brut, sinon = tare
        if (isInvertedProcess) {
          return !!workflowState.grossWeight;
        } else {
          return !!workflowState.tareWeight;
        }
      default:
        return false;
    }
  }, [workflowState, formData]);

  // Fonction pour mettre à jour le code de pesée depuis une pesée chargée
  const updateWeighingCode = useCallback((reference: string, weighingId?: string) => {
    setWorkflowState(prev => ({
      ...prev,
      weighingCode: reference,
      ...(weighingId ? { weighingId: weighingId.toString() } : {})
    }));
  }, []);

  return {
    // État
    workflowState,
    formData,
    isLoading,
    error,
    
    // Actions
    saveGrossWeight,
    saveQualityAnalysis,
    saveTareWeight,
    archiveWeighing,
    
    // Navigation
    goToStep,
    nextStep,
    previousStep,
    
    // Utilitaires
    updateFormData,
    resetWorkflow,
    canProceedToNextStep,
    refreshCurrentWeighing,
    isQualityAnalysisRequired,
    updateWeighingCode, // Nouvelle fonction pour mettre à jour le code
    
    // Nouvelles fonctions pour les processus
    getStepOrder,
    getStepLabel,
    getStepDescription,
    isWeighingStep,
    getCurrentWeighingType,
    getStepInstructions,
    initializeWeighingProcess,
    switchWeighingProcess,
    determineWeighingProcess,
    
    // État du processus
    weighingProcess: workflowState.weighingProcess
  };
};

export default useWeighingWorkflow;
